/**
 * Reliability Reporting Page - Phase 14
 * 
 * Manager-facing dashboard for merchant/location/terminal reliability metrics.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  AlertTriangle,
  CheckCircle,
  Clock,
  Building2,
  Monitor,
  RefreshCw,
  Download,
  ChevronRight,
  FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  getMerchantReliabilitySummary,
  getReliabilityTrends,
  getRepeatedIssueEntities,
  getBlockedPaymentReliability,
  getResolutionTimeMetrics,
  getReliabilityStatusBreakdown
} from '@/lib/reliability/reliabilitySelectors';
import {
  RELIABILITY_STATUS,
  RELIABILITY_STATUS_CONFIG,
  computeAverageResolutionTime
} from '@/lib/reliability/reliabilityModel';
import {
  exportReliabilitySummaryCsv,
  exportReliabilitySummaryJson,
  exportEntityTableCsv,
  exportBlockedPaymentFollowupCsv
} from '@/lib/reliability/exportEngine';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

export default function ReliabilityReportingPage() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [repeatedIssues, setRepeatedIssues] = useState(null);
  const [blockedPayments, setBlockedPayments] = useState(null);
  const [resolutionMetrics, setResolutionMetrics] = useState(null);
  const [statusBreakdown, setStatusBreakdown] = useState(null);
  
  const [filters, setFilters] = useState({
    merchantId: null,
    locationId: null,
    days: 7
  });
  
  const [activeTab, setActiveTab] = useState('overview');

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        summaryData,
        trendsData,
        repeatedData,
        blockedData,
        resolutionData,
        breakdownData
      ] = await Promise.all([
        getMerchantReliabilitySummary(filters),
        getReliabilityTrends({ ...filters, days: filters.days }),
        getRepeatedIssueEntities(filters),
        getBlockedPaymentReliability(filters),
        getResolutionTimeMetrics(filters),
        getReliabilityStatusBreakdown(filters)
      ]);
      
      setSummary(summaryData);
      setTrends(trendsData);
      setRepeatedIssues(repeatedData);
      setBlockedPayments(blockedData);
      setResolutionMetrics(resolutionData);
      setStatusBreakdown(breakdownData);
    } catch (error) {
      console.error('Failed to load reliability data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get status config
  const getStatusBadge = (status) => {
    const config = RELIABILITY_STATUS_CONFIG[status];
    const colorMap = {
      green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    
    return (
      <Badge className={colorMap[config?.color] || colorMap.green}>
        {config?.label || status}
      </Badge>
    );
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingDown className="h-4 w-4 text-green-500" />;
    if (trend === 'degrading') return <TrendingUp className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const handleExportSummary = () => {
    if (summary) {
      exportReliabilitySummaryCsv(summary);
    }
  };

  const handleExportJson = () => {
    if (summary) {
      exportReliabilitySummaryJson(summary);
    }
  };

  const handleExportLocations = () => {
    if (statusBreakdown?.locationStatuses) {
      exportEntityTableCsv(statusBreakdown.locationStatuses, 'location');
    }
  };

  const handleExportBlockedPayments = () => {
    exportBlockedPaymentFollowupCsv(filters);
  };

  if (loading && !summary) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]" data-testid="reliability-loading">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="reliability-reporting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Reliability Reporting
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Monitor offline sync health and recovery performance
          </p>
        </div>
        
        <div className="flex gap-2">
          <Select
            value={filters.days.toString()}
            onValueChange={(val) => setFilters({ ...filters, days: parseInt(val) })}
          >
            <SelectTrigger className="w-[140px]" data-testid="date-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Reliability Score */}
        <Card data-testid="reliability-score-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <BarChart3 className="h-8 w-8 text-blue-500" />
              {getStatusBadge(summary?.reliabilityStatus)}
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Reliability Score</p>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold">{summary?.reliabilityScore || 0}</p>
                <p className="text-sm text-gray-500">/ 100</p>
              </div>
              <Progress 
                value={summary?.reliabilityScore || 0} 
                className="mt-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* Open Incidents */}
        <Card data-testid="open-incidents-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <AlertTriangle className={`h-8 w-8 ${
                summary?.core?.unresolvedIncidentCount > 0 ? 'text-yellow-500' : 'text-green-500'
              }`} />
              {trends && getTrendIcon(trends.trend)}
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Unresolved Incidents</p>
              <p className="text-3xl font-bold">{summary?.core?.unresolvedIncidentCount || 0}</p>
              <p className="text-sm text-gray-500 mt-1">
                {summary?.core?.criticalIncidentCount || 0} critical
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Sync Success Rate */}
        <Card data-testid="sync-rate-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <CheckCircle className={`h-8 w-8 ${
                summary?.core?.syncSuccessRate >= 95 ? 'text-green-500' : 
                summary?.core?.syncSuccessRate >= 80 ? 'text-yellow-500' : 'text-red-500'
              }`} />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Sync Success Rate</p>
              <p className="text-3xl font-bold">
                {summary?.core?.syncSuccessRate?.toFixed(1) || 100}%
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {summary?.core?.conflictRate?.toFixed(1) || 0}% conflicts
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Avg Resolution Time */}
        <Card data-testid="resolution-time-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <Clock className="h-8 w-8 text-purple-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg Resolution Time</p>
              <p className="text-3xl font-bold">
                {computeAverageResolutionTime(summary?.core?.averageResolutionTimeMs)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                P90: {computeAverageResolutionTime(resolutionMetrics?.p90Ms)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Detailed Analysis</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExportSummary} data-testid="export-summary-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportJson} data-testid="export-json-btn">
                <Download className="h-4 w-4 mr-2" />
                Export JSON
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="trends">Trends</TabsTrigger>
              <TabsTrigger value="entities">Locations</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              <TabsTrigger value="repeated">Repeated Issues</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Core Metrics */}
                <div className="space-y-4">
                  <h4 className="font-medium">Core Metrics</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Total Incidents</span>
                      <span className="font-medium">{summary?.core?.offlineIncidentCount || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Blocked Payments</span>
                      <span className="font-medium text-red-600">{summary?.core?.blockedPaymentCount || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Affected Terminals</span>
                      <span className="font-medium">{summary?.core?.affectedTerminalsCount || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Affected Locations</span>
                      <span className="font-medium">{summary?.core?.affectedLocationsCount || 0}</span>
                    </div>
                  </div>
                </div>

                {/* POS Metrics */}
                <div className="space-y-4">
                  <h4 className="font-medium">POS Metrics</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Unsynced Orders</span>
                      <span className="font-medium">{summary?.pos?.unsyncedOrdersCount || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Order Replay Success</span>
                      <span className="font-medium">{summary?.pos?.orderReplaySuccessRate?.toFixed(1) || 100}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Payment Recovery</span>
                      <span className="font-medium">{summary?.pos?.paymentRecoverySuccessRate?.toFixed(1) || 100}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Failed Kitchen</span>
                      <span className="font-medium">{summary?.pos?.failedKitchenCount || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Staff Metrics */}
                <div className="space-y-4">
                  <h4 className="font-medium">Staff Metrics</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Offline Actions</span>
                      <span className="font-medium">{summary?.staff?.offlineStaffActionsCount || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Availability Sync</span>
                      <span className="font-medium">{summary?.staff?.availabilityReplaySuccessRate?.toFixed(1) || 100}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Schedule Ack Sync</span>
                      <span className="font-medium">{summary?.staff?.scheduleAckReplaySuccessRate?.toFixed(1) || 100}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">BackOffice Sync</span>
                      <span className="font-medium">{summary?.staff?.backofficeReplaySuccessRate?.toFixed(1) || 100}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends" className="space-y-6 pt-4">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm text-gray-500">Trend Direction:</span>
                <div className="flex items-center gap-2">
                  {getTrendIcon(trends?.trend)}
                  <span className="capitalize">{trends?.trend || 'stable'}</span>
                </div>
                <span className="text-sm text-gray-500 ml-auto">
                  Avg: {trends?.avgIncidentsPerDay?.toFixed(1) || 0} incidents/day
                </span>
              </div>

              {/* Simple bar chart representation */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-gray-500">Daily Incidents</h4>
                <div className="grid grid-cols-7 gap-2">
                  {trends?.daily?.map((day, idx) => (
                    <div key={idx} className="text-center">
                      <div className="h-24 bg-gray-100 dark:bg-gray-800 rounded relative flex items-end justify-center">
                        <div
                          className={`w-full rounded-t ${
                            day.criticalIncidents > 0 ? 'bg-red-500' : 'bg-blue-500'
                          }`}
                          style={{
                            height: `${Math.min(day.incidents * 10, 100)}%`,
                            minHeight: day.incidents > 0 ? '4px' : '0'
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                      </p>
                      <p className="text-xs font-medium">{day.incidents}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Blocked Payments Trend */}
              <div className="mt-6 space-y-2">
                <h4 className="font-medium text-sm text-gray-500">Daily Blocked Payments</h4>
                <div className="grid grid-cols-7 gap-2">
                  {trends?.daily?.map((day, idx) => (
                    <div key={idx} className="text-center">
                      <div className="h-16 bg-gray-100 dark:bg-gray-800 rounded relative flex items-end justify-center">
                        <div
                          className="w-full bg-red-400 rounded-t"
                          style={{
                            height: `${Math.min(day.blockedPayments * 20, 100)}%`,
                            minHeight: day.blockedPayments > 0 ? '4px' : '0'
                          }}
                        />
                      </div>
                      <p className="text-xs font-medium mt-1">{day.blockedPayments}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Entities Tab */}
            <TabsContent value="entities" className="space-y-4 pt-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium">Location Status Breakdown</h4>
                <Button variant="outline" size="sm" onClick={handleExportLocations} data-testid="export-locations-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>

              {/* Status distribution */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                {Object.entries(statusBreakdown?.breakdown || {}).map(([status, count]) => {
                  const config = RELIABILITY_STATUS_CONFIG[status];
                  return (
                    <div key={status} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
                      <p className="text-sm text-gray-500">{config?.label}</p>
                      <p className="text-2xl font-bold">{count}</p>
                    </div>
                  );
                })}
              </div>

              {/* Location table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b dark:border-gray-700">
                      <th className="text-left py-3 px-4">Location</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-right py-3 px-4">Score</th>
                      <th className="text-right py-3 px-4">Incidents</th>
                      <th className="text-right py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusBreakdown?.locationStatuses?.slice(0, 10).map((loc) => (
                      <tr key={loc.locationId} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span>{loc.locationId}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(loc.status)}</td>
                        <td className="py-3 px-4 text-right font-medium">{loc.score}</td>
                        <td className="py-3 px-4 text-right">{loc.incidentCount}</td>
                        <td className="py-3 px-4 text-right">
                          <a
                            href={`${bo('support-diagnostics')}?locationId=${loc.locationId}`}
                            className="text-blue-600 hover:underline"
                          >
                            Diagnostics
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(statusBreakdown?.totalLocations || 0) === 0 && (
                <p className="text-center text-gray-500 py-8">No location data available</p>
              )}
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments" className="space-y-6 pt-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Blocked Payment Reliability</h4>
                <Button variant="outline" size="sm" onClick={handleExportBlockedPayments} data-testid="export-blocked-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Export Follow-up List
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-500">Total Payment Incidents</p>
                  <p className="text-2xl font-bold">{blockedPayments?.totalPaymentIncidents || 0}</p>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-sm text-gray-500">Currently Blocked</p>
                  <p className="text-2xl font-bold text-red-600">{blockedPayments?.blockedCount || 0}</p>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm text-gray-500">Resolved</p>
                  <p className="text-2xl font-bold text-green-600">{blockedPayments?.resolvedCount || 0}</p>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-gray-500">Recovery Rate</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {blockedPayments?.recoveryRate?.toFixed(1) || 100}%
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">Sync Success Rate</p>
                <Progress value={blockedPayments?.syncSuccessRate || 100} className="h-3" />
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {blockedPayments?.syncSuccessRate?.toFixed(1) || 100}%
                </p>
              </div>
            </TabsContent>

            {/* Repeated Issues Tab */}
            <TabsContent value="repeated" className="space-y-6 pt-4">
              <p className="text-sm text-gray-500">
                Locations and terminals with 3+ incidents in the selected period
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Repeated Issue Locations */}
                <div>
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Problem Locations ({repeatedIssues?.totalRepeatedLocations || 0})
                  </h4>
                  <div className="space-y-2">
                    {repeatedIssues?.locations?.slice(0, 5).map((loc) => (
                      <div
                        key={loc.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{loc.id}</p>
                          <p className="text-xs text-gray-500">
                            {loc.critical} critical, {loc.blocked} blocked
                          </p>
                        </div>
                        <Badge variant="outline">{loc.count} incidents</Badge>
                      </div>
                    ))}
                    {(repeatedIssues?.locations?.length || 0) === 0 && (
                      <p className="text-sm text-gray-500 py-4 text-center">No repeated issues</p>
                    )}
                  </div>
                </div>

                {/* Repeated Issue Terminals */}
                <div>
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    Problem Terminals ({repeatedIssues?.totalRepeatedTerminals || 0})
                  </h4>
                  <div className="space-y-2">
                    {repeatedIssues?.terminals?.slice(0, 5).map((term) => (
                      <div
                        key={term.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{term.id}</p>
                          <p className="text-xs text-gray-500">
                            Location: {term.locationId || 'Unknown'}
                          </p>
                        </div>
                        <Badge variant="outline">{term.count} incidents</Badge>
                      </div>
                    ))}
                    {(repeatedIssues?.terminals?.length || 0) === 0 && (
                      <p className="text-sm text-gray-500 py-4 text-center">No repeated issues</p>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Cross-links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Related Tools</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href={bo('support-diagnostics')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-support-diagnostics"
            >
              <div>
                <p className="font-medium">Support Diagnostics</p>
                <p className="text-sm text-gray-500">Generate diagnostic bundles</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </a>
            
            <a
              href={bo('incident-audit')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-incident-audit"
            >
              <div>
                <p className="font-medium">Incident Audit Trail</p>
                <p className="text-sm text-gray-500">View detailed incident history</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </a>
            
            <a
              href={bo('pos-reconciliation')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-pos-reconciliation"
            >
              <div>
                <p className="font-medium">POS Reconciliation</p>
                <p className="text-sm text-gray-500">Review POS offline recovery</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400" />
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
